// The live session, from the phone (spec/15 P-01, P-10; ADR-0054): the summary card Home shows, and the actions
// Play offers. Every action is the server's: the same lock, unlock, pin, call and log out the PC's kiosk does, on the
// player's own session. Lock and unlock are asked of the PC and land when it does them. No time is added from the phone
// (owner, 2026-10-11): a pass bought from the wallet (`BuyPass.tsx`) is what adds play here. Each answer replaces the
// cached live session, so the screen follows at once.

import type { MyLiveSession, StationCallCategory } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Badge, Button, Dialog, RadioGroup, TextField, SessionRing, useToast } from '@clavian/ui-web';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import { useApi } from '../api/client';
import { PROBLEM, problemCode } from '../api/problems';
import { queryKeys } from '../api/queries';
import { useProblem } from '../api/use-problem';
import { clock, useCountdown } from '../lib/format';
import { NavIcon } from '../shell/nav-icons';

/** Whether the session's clock is running (billing goes on while locked; a paused lock is the server's to say). */
export const isRunning = (live: MyLiveSession): boolean => live.session.status === 'active' || live.session.status === 'grace' || live.session.status === 'locked';

/** The countdown of the live session, against the server's clock. */
export function useLiveLeft(live: MyLiveSession | undefined): number | undefined {
  return useCountdown(live?.session.timeLeftSeconds, live?.session.serverTime, live !== undefined && isRunning(live));
}

/** How full the ring is: the time left over the time left plus the time played so far. */
function ringValue(live: MyLiveSession, left: number): number {
  const played = Math.max(0, (Date.parse(live.session.serverTime) - Date.parse(live.session.startedAt)) / 1000);
  return left + played <= 0 ? 0 : left / (left + played);
}

export function ringTone(live: MyLiveSession, left: number): 'session' | 'warn' | 'danger' | 'muted' {
  if (live.session.status === 'locked') return 'muted';
  if (live.session.status === 'grace' || left <= 5 * 60) return 'danger';
  return left <= 15 * 60 ? 'warn' : 'session';
}

/** The ring with the countdown inside it. */
export function LiveRing({ live, left, size }: { readonly live: MyLiveSession; readonly left: number; readonly size: number }): ReactNode {
  const { t } = useTranslation('player');
  return (
    <SessionRing value={ringValue(live, left)} label={t('play.live.timeLeft')} tone={ringTone(live, left)} size={size} thickness={size > 150 ? 10 : 6}>
      <span className="pw-ring__inner">
        <span className="pw-ring__time clav-num" aria-live="off">
          {clock(left)}
        </span>
        <span className="pw-ring__label">{t('home.live.left')}</span>
      </span>
    </SessionRing>
  );
}

export function statusBadge(live: MyLiveSession, t: (key: string) => string): ReactNode {
  const status = live.session.status;
  const tone = status === 'locked' ? 'neutral' : status === 'grace' ? 'danger' : 'session';
  return (
    <Badge tone={tone} dot>
      {t(`play.live.status.${status}`)}
    </Badge>
  );
}

/** The actions on the live session, each refreshing what it changed. */
export function useLiveActions() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t } = useTranslation('player');
  const problem = useProblem();
  const { show } = useToast();

  const settle = async (live?: MyLiveSession) => {
    if (live !== undefined) queryClient.setQueryData(queryKeys.live, { live, serverTime: live.session.serverTime });
    await queryClient.invalidateQueries({ queryKey: queryKeys.live });
    await queryClient.invalidateQueries({ queryKey: queryKeys.passes });
  };
  const onError = (error: unknown) => {
    // A session the staff run cannot be changed from the phone: that screen says so; every other refusal says its own.
    const title = problemCode(error) === PROBLEM.conflict ? t('play.failure.staffRun') : problem.text(error, { fallback: t('play.failure.generic') });
    show({ title, tone: 'danger' });
    void queryClient.invalidateQueries({ queryKey: queryKeys.live });
  };

  // The PC locks and unlocks itself and the session follows it: the answer is usually the request on its way, and the
  // screen polls quickly until the PC has done it (or says it did not).
  const lock = useMutation({
    mutationFn: (pin: string | undefined) => api.lockMyPlaySession({ body: pin === undefined || pin === '' ? {} : { pin } }),
    onSuccess: async (live) => {
      await settle(live);
      show({ title: live.lockRequest?.state === 'pending' ? t('play.actions.lockAsked') : t('play.actions.locked'), tone: 'ok' });
    },
    onError,
  });
  const unlock = useMutation({
    mutationFn: () => api.unlockMyPlaySession(),
    onSuccess: async (live) => {
      await settle(live);
      show({ title: live.lockRequest?.state === 'pending' ? t('play.actions.unlockAsked') : t('play.actions.unlocked'), tone: 'ok' });
    },
    onError,
  });
  const pin = useMutation({
    mutationFn: (holdingId: string) => api.pinPassOnMyPlaySession({ path: { holdingId } }),
    onSuccess: async (live) => settle(live),
    onError,
  });
  const call = useMutation({
    mutationFn: (body: { category: StationCallCategory; note?: string }) => api.callStaffFromMyPlaySession({ body }),
    onSuccess: async () => {
      await settle();
      show({ title: t('play.call.sent'), tone: 'ok' });
    },
    onError,
  });
  const cancelCall = useMutation({
    mutationFn: () => api.cancelMyStaffCall(),
    onSuccess: async () => {
      await settle();
      show({ title: t('play.call.cancelled'), tone: 'ok' });
    },
    onError,
  });
  const end = useMutation({
    mutationFn: () => api.endMyPlaySession(),
    onSuccess: async () => {
      queryClient.setQueryData(queryKeys.live, { serverTime: new Date().toISOString() });
      await queryClient.invalidateQueries({ queryKey: queryKeys.live });
      await queryClient.invalidateQueries({ queryKey: queryKeys.playSessions });
      await queryClient.invalidateQueries({ queryKey: queryKeys.orders });
    },
    onError,
  });
  return { lock, unlock, pin, call, cancelCall, end };
}

/** A lock or unlock asked for from the phone that the PC has not done yet. */
export const lockPending = (live: MyLiveSession): boolean => live.lockRequest?.state === 'pending';

/** What the phone's lock or unlock is doing: on its way to the PC, or not done by it. Nothing once the PC did it. */
export function LockRequestNote({ live }: { readonly live: MyLiveSession }): ReactNode {
  const { t } = useTranslation('player');
  const request = live.lockRequest;
  if (request === undefined) return null;
  if (request.state === 'pending') {
    return (
      <p className="pw-play__note" role="status">
        {request.action === 'lock' ? t('play.actions.locking') : t('play.actions.unlocking')}
      </p>
    );
  }
  return (
    <p className="pw-play__note" role="alert">
      {request.action === 'lock' ? t('play.actions.lockFailed') : t('play.actions.unlockFailed')}
    </p>
  );
}

/** Home's card: where you are playing, the countdown, and the one quick control. */
export function LiveSessionCard({ live }: { readonly live: MyLiveSession }): ReactNode {
  const { t } = useTranslation('player');
  const left = useLiveLeft(live) ?? live.session.timeLeftSeconds;
  const actions = useLiveActions();
  const locked = live.session.status === 'locked';

  return (
    <section className="pw-live" aria-labelledby="pw-live-title" data-status={live.session.status}>
      <div className="pw-live__ring">
        <LiveRing live={live} left={left} size={112} />
      </div>
      <div className="pw-live__body">
        <p className="pw-live__eyebrow">{t('home.live.title')}</p>
        <h2 id="pw-live-title" className="pw-live__where">
          {t('home.live.on', { station: live.station.name, venue: live.venue.name })}
        </h2>
        <div className="pw-live__status">{statusBadge(live, t)}</div>
        <div className="pw-live__actions">
          <Link to="/play" className="pw-btn pw-btn--primary">
            {t('home.live.open')}
          </Link>
          {live.remoteLock ? (
            <Button
              variant="secondary"
              icon={<NavIcon name={locked ? 'unlock' : 'lock'} size={18} />}
              busy={actions.lock.isPending || actions.unlock.isPending || lockPending(live)}
              onClick={() => (locked ? actions.unlock.mutate() : actions.lock.mutate(undefined))}
            >
              {locked ? t('home.live.unlock') : t('home.live.lock')}
            </Button>
          ) : null}
        </div>
        <LockRequestNote live={live} />
      </div>
    </section>
  );
}

/** "Lock your PC?", with an optional PIN for unlocking at the PC. */
export function LockDialog({ open, onOpenChange }: { readonly open: boolean; readonly onOpenChange: (open: boolean) => void }): ReactNode {
  const { t } = useTranslation('player');
  const { lock } = useLiveActions();
  const [pin, setPin] = useState('');
  const invalid = pin !== '' && !/^\d{4,6}$/.test(pin);
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('play.lock.title')}
      description={t('play.lock.body')}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('play.lock.cancel')}
          </Button>
          <Button
            variant="primary"
            busy={lock.isPending}
            disabled={invalid}
            onClick={() => lock.mutate(pin, { onSuccess: () => onOpenChange(false) })}
          >
            {t('play.lock.confirm')}
          </Button>
        </>
      }
    >
      <TextField
        label={t('play.lock.pin')}
        description={t('play.lock.pinHint')}
        showOptional
        inputMode="numeric"
        autoComplete="off"
        maxLength={6}
        value={pin}
        error={invalid ? t('play.lock.pinInvalid') : undefined}
        onChange={(event) => setPin(event.target.value.replace(/\D+/g, ''))}
      />
    </Dialog>
  );
}

const CATEGORIES: readonly StationCallCategory[] = ['pc', 'peripherals', 'food', 'other'];

/** Call staff to the PC, with what it is about. */
export function CallStaffDialog({
  live,
  open,
  onOpenChange,
}: {
  readonly live: MyLiveSession;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}): ReactNode {
  const { t } = useTranslation('player');
  const { call } = useLiveActions();
  const [category, setCategory] = useState<StationCallCategory>('pc');
  const [note, setNote] = useState('');
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('play.call.title')}
      description={t('play.call.body', { station: live.station.name })}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('play.call.cancel')}
          </Button>
          <Button
            variant="primary"
            busy={call.isPending}
            onClick={() =>
              call.mutate(
                { category, ...(note.trim() === '' ? {} : { note: note.trim() }) },
                { onSuccess: () => onOpenChange(false) },
              )
            }
          >
            {t('play.call.send')}
          </Button>
        </>
      }
    >
      <div className="pw-form">
        <RadioGroup
          label={t('play.call.category')}
          value={category}
          onValueChange={(value) => setCategory(value as StationCallCategory)}
          options={CATEGORIES.map((value) => ({ value, label: t(`play.call.categories.${value}`) }))}
        />
        <TextField
          label={t('play.call.note')}
          description={t('play.call.noteHint')}
          showOptional
          maxLength={140}
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
      </div>
    </Dialog>
  );
}

/** "Log out of PC 07?" */
export function EndDialog({
  live,
  open,
  onOpenChange,
}: {
  readonly live: MyLiveSession;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}): ReactNode {
  const { t } = useTranslation('player');
  const { show } = useToast();
  const { end } = useLiveActions();
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('play.end.title', { station: live.station.name })}
      description={t('play.end.body')}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('play.end.cancel')}
          </Button>
          <Button
            variant="danger"
            busy={end.isPending}
            onClick={() =>
              end.mutate(undefined, {
                onSuccess: () => {
                  show({ title: t('play.end.done', { station: live.station.name }), tone: 'ok' });
                  onOpenChange(false);
                },
              })
            }
          >
            {t('play.end.confirm')}
          </Button>
        </>
      }
    />
  );
}
